import { api } from "../../lib/api";
import type { SaleItem, SalePayment } from "../sales/api";

export type ConsignmentRefund = {
  id: number;
  paymentId: number | null;
  paymentMethodId: number;
  amountCents: number;
  reason: string | null;
  operatorId: number;
  createdAt: string;
};

export type Consignment = {
  id: number;
  createdAt: string;
  operatorId: number;
  status: "active" | "voided";
  operationType: "consignment";
  consignmentStatus: "open" | "converted" | "discarded";
  items: SaleItem[];
  payments: Array<SalePayment & { isConsignmentAdvance: boolean }>;
  refunds: ConsignmentRefund[];
  itemsSubtotalCents: number;
  lineDiscountTotalCents: number;
  globalDiscountCents: number;
  paymentMethodDiscountCents: number;
  totalChargedCents: number;
  totalPaidCents: number;
  totalRefundedCents: number;
  balanceCents: number;
  netReceivedCents: number | null;
  commissionCents: number;
  paymentMethodId: number | null;
  paymentMethodSnapshot: SalePayment["paymentMethodSnapshot"] | null;
  customerId: number;
  customerNameSnapshot: string | null;
  note: string | null;
  paymentMethodDiscountApplied: boolean;
  createdFromClientId: string | null;
};

export type ConsignmentItemInput = {
  productId: number;
  productName: string;
  unitPriceCents: number;
  qty: number;
  lineDiscountCents?: number;
  sizeGuideId?: number | null;
  size?: string | null;
  note?: string | null;
};

export type ConsignmentPaymentInput = {
  paymentMethodId: number;
  amountCents: number;
  netReceivedCents?: number;
  skipNetReceived?: boolean;
};

export type CreateConsignmentInput = {
  clientId: string;
  customerId: number;
  globalDiscountCents?: number;
  note?: string;
  items: ConsignmentItemInput[];
  payments?: ConsignmentPaymentInput[];
  /** Token returned by the API after reviewing an unusual monetary amount. */
  moneyConfirmationToken?: string;
};

export type UpdateConsignmentInput = Omit<CreateConsignmentInput, "clientId" | "payments"> & {
  newPayments?: ConsignmentPaymentInput[];
};

export type ConvertConsignmentInput = {
  items: ConsignmentItemInput[];
  globalDiscountCents?: number;
  payment?: (ConsignmentPaymentInput & { accountCurrent?: boolean }) | null;
  moneyConfirmationToken?: string;
};

export type RefundConsignmentInput = {
  allocations: Array<{ paymentId?: number; paymentMethodId: number; amountCents: number }>;
  reason?: string;
  moneyConfirmationToken?: string;
};

export const consignmentsApi = {
  create: (body: CreateConsignmentInput) =>
    api<Consignment>("/api/consignments", { method: "POST", body }),
  list: (status: "open" | "converted" | "discarded" = "open") =>
    api<Consignment[]>(`/api/consignments?status=${status}`),
  get: (id: number) => api<Consignment>(`/api/consignments/${id}`),
  update: (id: number, body: UpdateConsignmentInput) =>
    api<Consignment>(`/api/consignments/${id}`, { method: "PATCH", body }),
  convert: (id: number, body: ConvertConsignmentInput) =>
    api<Consignment>(`/api/consignments/${id}/convert`, { method: "POST", body }),
  refund: (id: number, body: RefundConsignmentInput) =>
    api<Consignment>(`/api/consignments/${id}/refund`, { method: "POST", body }),
  discard: (id: number) =>
    api<Consignment>(`/api/consignments/${id}/discard`, { method: "POST", body: {} }),
};
